import React, { useState, useMemo, useCallback, memo, useRef, useEffect } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Checkbox } from "../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { RichTextEditor } from "../RichTextEditor";
import { InternalControlData } from "../types";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { cn } from "../../../lib/utils";
import { ControlDocumentManager } from "../ControlDocumentManager";
import { MultiSelectDropdown } from "../../ui/multi-select-dropdown";

interface ControlSummaryProps {
  data: InternalControlData;
  onChange: (data: Partial<InternalControlData>) => void;
  controlId?: string;
}

type PaneType = "control-details" | "related-accounts" | "detailed-description";

interface RelatedAccount {
  par: string;
  note_line_id: string;
  note_line: string;
  amount: number;
}

// Separate component for Related Accounts Table to isolate state
// Not memoized to avoid comparison issues - component is isolated and won't affect parent
const RelatedAccountsTable: React.FC<{ workspaces: string[] }> = ({ workspaces }) => {
  const [accounts, setAccounts] = useState<RelatedAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLoad = async () => {
    if (!workspaces || workspaces.length === 0) {
      setError("No workspaces linked. Please link workspaces in the Control Details tab.");
      setAccounts([]);
      return;
    }

    setLoading(true);
    setError(null);
    setAccounts([]);

    try {
      if (window.sharePointAPI && typeof window.sharePointAPI.fetchRelatedAccounts === "function") {
        const result = await window.sharePointAPI.fetchRelatedAccounts(workspaces);

        if (result.success && result.data) {
          const rows = Array.isArray(result.data) ? result.data : [];
          const mappedAccounts: RelatedAccount[] = rows.map((item: any) => ({
            par: item.par || "",
            note_line_id: item.note_line_id || "",
            note_line: item.note_line || "",
            amount: typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0,
          }));
          setAccounts(mappedAccounts);
          setError(null);
        } else {
          setError(result.error || "Failed to fetch related accounts");
          setAccounts([]);
        }
      } else {
        setError("SharePoint API not available");
        setAccounts([]);
      }
    } catch (err) {
      console.error("Error fetching related accounts:", err);
      setError(err instanceof Error ? err.message : "Unknown error occurred");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-white">Related Accounts and Areas</Label>
        <Button 
          onClick={handleLoad}
          disabled={loading}
          className="bg-primary text-white hover:bg-primary/80"
          data-allow-archival-edit={true}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </>
          ) : (
            "Load Table"
          )}
        </Button>
      </div>
      
      {loading ? (
        <div className="p-8 border border-white/10 rounded-lg bg-black/20 text-center text-white/60">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          <p>Loading related accounts...</p>
        </div>
      ) : error ? (
        <div className="p-8 border border-red-500/20 rounded-lg bg-red-500/10 text-center text-red-400">
          <p>Error: {error}</p>
        </div>
      ) : accounts.length === 0 ? (
        <div className="p-8 border border-white/10 rounded-lg bg-black/20 text-center text-white/60">
          {workspaces.length === 0 
            ? "No workspaces linked. Please link workspaces in the Control Details tab."
            : "Click 'Load Table' to fetch related accounts data."}
        </div>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-black/20">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/60">PAR</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Note Line ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Note Line</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-white/60">Amount</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account, index) => (
                <tr key={index} className="border-t border-white/10 hover:bg-white/5">
                  <td className="px-4 py-3 text-white">{account.par}</td>
                  <td className="px-4 py-3 text-white">{account.note_line_id || ""}</td>
                  <td className="px-4 py-3 text-white">{account.note_line || ""}</td>
                  <td className="px-4 py-3 text-right text-white">
                    {account.amount.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const ControlSummary: React.FC<ControlSummaryProps> = ({ data, onChange, controlId }) => {
  const [activePane, setActivePane] = useState<PaneType>("control-details");
  const [sections, setSections] = useState<string[]>([]);
  const [loadingSections, setLoadingSections] = useState(false);
  
  // Use workspaces directly - memoization was causing issues
  const linkedWorkspaces = data.workspaceLinked || [];
  const relatedAccounts = data.relatedAccounts || [];

  // Use ref to access latest onChange without causing callback recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Load sections from Sections.json when component mounts
  useEffect(() => {
    const loadSections = async () => {
      setLoadingSections(true);
      try {
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadSectionsJson === 'function') {
          const result = await window.sharePointAPI.downloadSectionsJson();
          
          if (result.success && result.data) {
            // Extract all section names from Planning, Execution, and ConcludingProcedures
            const allSections: string[] = [];
            
            if (result.data.Planning) {
              allSections.push(...Object.keys(result.data.Planning));
            }
            if (result.data.Execution) {
              allSections.push(...Object.keys(result.data.Execution));
            }
            if (result.data.ConcludingProcedures) {
              allSections.push(...Object.keys(result.data.ConcludingProcedures));
            }
            
            // Remove duplicates and sort
            const uniqueSections = Array.from(new Set(allSections)).sort();
            setSections(uniqueSections);
            console.log('Loaded sections from Sections.json:', uniqueSections.length, 'sections');
          } else {
            console.error('Failed to load sections:', result.error);
            setSections([]);
          }
        } else {
          console.warn('SharePoint API not available');
          setSections([]);
        }
      } catch (error) {
        console.error('Error loading sections:', error);
        setSections([]);
      } finally {
        setLoadingSections(false);
      }
    };

    loadSections();
  }, []);

  // Stabilize callbacks for ControlDocumentManager to prevent infinite loops
  const handleIucDocumentsChange = useCallback((documents: typeof data.iucDocuments) => {
    onChangeRef.current({ iucDocuments: documents });
  }, []);

  const handleIucTextResponseChange = useCallback((textResponse: typeof data.iucTextResponse) => {
    onChangeRef.current({ iucTextResponse: textResponse });
  }, []);

  const handleOutputDocumentsChange = useCallback((documents: typeof data.outputDocuments) => {
    onChangeRef.current({ outputDocuments: documents });
  }, []);

  const handleOutputTextResponseChange = useCallback((textResponse: typeof data.outputTextResponse) => {
    onChangeRef.current({ outputTextResponse: textResponse });
  }, []);

  // Memoize callbacks to prevent Select components from re-rendering unnecessarily
  // Use onChangeRef to avoid dependency on onChange prop
  const handleTypeOfControlChange = useCallback((value: "Direct" | "Indirect" | "GITC") => {
    onChangeRef.current({ typeOfControl: value });
  }, []);

  const handleApproachChange = useCallback((value: "Preventive" | "Detective") => {
    onChangeRef.current({ approach: value });
  }, []);

  const handleWorkspaceLinkedChange = useCallback((selected: string[]) => {
    onChangeRef.current({ workspaceLinked: selected });
  }, []);

  // Get stable values - use string directly, not memoized
  const typeOfControlValue = data.typeOfControl || "Direct";
  const approachValue = data.approach || "Preventive";


  const handleControlTypeChange = (key: keyof typeof data.controlTypes, checked: boolean) => {
    onChange({
      controlTypes: {
        ...data.controlTypes,
        [key]: checked,
      },
    });
  };

  const addStep = () => {
    const newStep = {
      id: `step-${Date.now()}`,
      content: "",
    };
    onChange({ steps: [...data.steps, newStep] });
  };

  const removeStep = (id: string) => {
    onChange({ steps: data.steps.filter((s) => s.id !== id) });
  };

  const updateStep = (id: string, content: string) => {
    onChange({
      steps: data.steps.map((s) => (s.id === id ? { ...s, content } : s)),
    });
  };


  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">Control Summary</h2>
      </div>
      <div className="p-6">
        {/* Pane Navigation */}
        <div className="flex space-x-1 bg-black/20 rounded-lg p-1 mb-6">
          <button
            onClick={() => setActivePane("control-details")}
            className={cn(
              "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all",
              activePane === "control-details"
                ? "bg-primary text-white"
                : "text-white/60 hover:text-white hover:bg-white/10"
            )}
          >
            Control Details
          </button>
          <button
            onClick={() => setActivePane("related-accounts")}
            className={cn(
              "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all",
              activePane === "related-accounts"
                ? "bg-primary text-white"
                : "text-white/60 hover:text-white hover:bg-white/10"
            )}
          >
            Related Accounts and Areas
          </button>
          <button
            onClick={() => setActivePane("detailed-description")}
            className={cn(
              "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all",
              activePane === "detailed-description"
                ? "bg-primary text-white"
                : "text-white/60 hover:text-white hover:bg-white/10"
            )}
          >
            Detailed Description
          </button>
        </div>

        {/* Control Details Pane */}
        {activePane === "control-details" && (
          <div className="space-y-6">
            {/* Control ID */}
            <div className="space-y-2">
              <Label className="text-white">Control ID</Label>
              <Input
                value={data.controlId}
                disabled
                className="bg-black/20 text-white/60 cursor-not-allowed border-white/10"
              />
            </div>

            {/* Control Name */}
            <div className="space-y-2">
              <Label className="text-white">Control Name</Label>
              <Input
                value={data.controlName || ""}
                onChange={(e) => onChange({ controlName: e.target.value })}
                className="bg-black/20 text-white border-white/10"
                placeholder="Enter control name..."
              />
            </div>

            {/* Workspace Linked */}
            <div className="space-y-2">
              <Label className="text-white">
                Workspace Linked <span className="text-white/60">(Select all applicable)</span>
              </Label>
              <MultiSelectDropdown
                options={sections}
                value={linkedWorkspaces}
                onChange={handleWorkspaceLinkedChange}
                placeholder="Select workspaces..."
                loading={loadingSections}
              />
            </div>

            {/* Control Description */}
            <RichTextEditor
              label="Control Description"
              value={data.controlDescription}
              onChange={(content) => onChange({ controlDescription: content })}
              placeholder="Enter control description..."
            />

            {/* Control Attributes */}
            <div className="space-y-4 border-t border-white/10 pt-6">
              <h4 className="text-sm font-medium text-white">Control Attributes</h4>

              {/* Type of Control */}
              <div className="space-y-2">
                <Label className="text-white">Type of Control</Label>
                <select
                  value={typeOfControlValue}
                  onChange={(e) => handleTypeOfControlChange(e.target.value as "Direct" | "Indirect" | "GITC")}
                  className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
                >
                  <option value="Direct">Direct</option>
                  <option value="Indirect">Indirect</option>
                  <option value="GITC">GITC</option>
                </select>
              </div>

              {/* Nature */}
              <div className="space-y-2">
                <Label className="text-white">Nature</Label>
                <Input
                  value={data.nature}
                  disabled
                  className="bg-black/20 text-white/60 cursor-not-allowed border-white/10"
                />
              </div>

              {/* Approach */}
              <div className="space-y-2">
                <Label className="text-white">Approach</Label>
                <select
                  value={approachValue}
                  onChange={(e) => handleApproachChange(e.target.value as "Preventive" | "Detective")}
                  className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
                >
                  <option value="Preventive">Preventive</option>
                  <option value="Detective">Detective</option>
                </select>
              </div>

              {/* Type (Checkboxes) */}
              <div className="space-y-2">
                <Label className="text-white">Type</Label>
                <div className="space-y-2">
                  {[
                    { key: "verification", label: "Verification" },
                    { key: "authorizationsApprovals", label: "Authorizations & Approvals" },
                    { key: "physicalControlsCounts", label: "Physical Controls & Counts" },
                    { key: "controlsOverIUC", label: "Controls over IUC" },
                    { key: "reconciliations", label: "Reconciliations" },
                    { key: "controlWithReviewElement", label: "Control with review element" },
                    { key: "gitcControl", label: "GITC Control" },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={data.controlTypes[key as keyof typeof data.controlTypes]}
                        onCheckedChange={(checked) =>
                          handleControlTypeChange(key as keyof typeof data.controlTypes, checked as boolean)
                        }
                      />
                      <span className="text-sm text-white">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Tailoring Questions */}
            <div className="space-y-4 border-t border-white/10 pt-6">
              <h4 className="text-sm font-medium text-white">Tailoring Questions</h4>

              <div className="flex items-center justify-between">
                <Label className="flex-1 text-white">The control addresses a significant risk.</Label>
                <Switch
                  checked={data.addressesSignificantRisk}
                  onCheckedChange={(checked) => onChange({ addressesSignificantRisk: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="flex-1 text-white">
                  The control includes a review element and addresses a significant accounting estimate.
                </Label>
                <Switch
                  checked={data.includesReviewElement}
                  onCheckedChange={(checked) => onChange({ includesReviewElement: checked })}
                />
              </div>
            </div>
          </div>
        )}

        {/* Related Accounts Pane */}
        {activePane === "related-accounts" && (
          <div className="space-y-6">
            {/* Pass workspaces directly - component is memoized and won't cause re-renders */}
            <RelatedAccountsTable workspaces={linkedWorkspaces} />
          </div>
        )}

        {/* Detailed Description Pane */}
        {activePane === "detailed-description" && (
          <div className="space-y-6">
            {/* Display Control Description */}
            <div className="space-y-2">
              <Label className="text-white">Control Description</Label>
              <div
                className="rounded-md border border-white/10 bg-black/20 p-3 text-white/60"
                dangerouslySetInnerHTML={{
                  __html: data.controlDescription || "No description entered yet.",
                }}
              />
            </div>

            {/* Detailed Description Toggle */}
            <div className="flex items-center justify-between border-t border-white/10 pt-6">
              <Label className="flex-1 text-white">
                Detailed description of how the control is expected to be performed
              </Label>
              <Switch
                checked={data.detailedDescriptionEnabled}
                onCheckedChange={(checked) => onChange({ detailedDescriptionEnabled: checked })}
              />
            </div>

            {/* Steps Container */}
            {data.detailedDescriptionEnabled && (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <p className="text-sm text-white/60">
                    Separately document each relevant step of the control performer's activities.
                  </p>
                  <Button onClick={addStep} className="bg-primary text-white hover:bg-primary/80">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Step
                  </Button>
                </div>

                <div className="space-y-4">
                  {data.steps.map((step, index) => (
                    <div
                      key={step.id}
                      className="p-4 border border-white/10 rounded-lg bg-black/20"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-white">
                          Step {index + 1}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStep(step.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <RichTextEditor
                        value={step.content}
                        onChange={(content) => updateStep(step.id, content)}
                        placeholder="Describe this step..."
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Information Used in Control (IUC) */}
            <div className="space-y-2 border-t border-white/10 pt-6">
              <Label className="text-white">Information used in control (IUC)</Label>
              <p className="text-xs text-white/60 mb-2">
                Associate information from information library
              </p>
              <ControlDocumentManager
                controlId={data.controlId}
                workspaceLinked={data.workspaceLinked}
                documents={data.iucDocuments || []}
                onDocumentsChange={handleIucDocumentsChange}
                textResponse={data.iucTextResponse}
                onTextResponseChange={handleIucTextResponseChange}
                label="IUC"
              />
            </div>

            {/* Outputs of the Control */}
            <div className="space-y-2 border-t border-white/10 pt-6">
              <Label className="text-white">Outputs of the control</Label>
              <p className="text-xs text-white/60 mb-2">
                Understand the output of the control, including evidence that supports the control
                activity occurred
              </p>
              <ControlDocumentManager
                controlId={data.controlId}
                workspaceLinked={data.workspaceLinked}
                documents={data.outputDocuments || []}
                onDocumentsChange={handleOutputDocumentsChange}
                textResponse={data.outputTextResponse}
                onTextResponseChange={handleOutputTextResponseChange}
                label="Outputs"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};